import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { toast, Bounce } from "react-toastify"
import { useSelector } from "react-redux"
import { get, post } from "../api/axios"
import endpoints from "../api/endpoints"
import { getApiErrorMessage, toastApiError } from "../helpers/apiError"
import {
  addSessionCollaboratorReaction,
  addSessionCollaborators,
  countsFromReactionRows,
  countsFromReactionSummary,
  emptyImpression,
  getSessionCollaboratorReactions,
  impressionFromReaction,
  removeSessionCollaboratorReaction,
  removeSessionCollaborators,
  SESSION_COLLAB_REACTION,
} from "../helpers/session"
import {
  DEFAULT_SORT_BY,
  DEFAULT_SORT_DIRECTION,
  MEMBERS_PAGE_SIZE,
  SEARCH_DEBOUNCE_MS,
  TIMELOG_REASONS,
  canAccessSessionTimelog,
  formatDateTime,
  formatSeconds,
  mapSessionCollaborator,
  mapSessionDetail,
  resolveLeaveSessionLoggedTime,
  resolveSessionDisplayTimer,
  mapTimelogResult,
  matchesSessionCollabStatusFilters,
  upsertSessionCollaboratorMember,
} from "../helpers/sessionDetail"
import { celebrateImpression } from "../components/DashboardComponents/ImpressionCelebration"
import useSessionCollaboratorRealtime from "./useSessionCollaboratorRealtime"

/**
 * Shared session-detail data + timer actions.
 * Used by the global session player so full and minimized views share state.
 */
const useSessionDetail = (sessionId, { project = null, city = null } = {}) => {
  const contextRef = useRef({ project, city })
  contextRef.current = { project, city }
  const [session, setSession] = useState(null)
  const [displayTimer, setDisplayTimer] = useState("00:00:00")
  const [isLoading, setIsLoading] = useState(Boolean(sessionId))
  const [error, setError] = useState(null)
  const [notFound, setNotFound] = useState(false)

  const [members, setMembers] = useState([])
  const [membersTotal, setMembersTotal] = useState(0)
  const [membersPage, setMembersPage] = useState(1)
  const [membersPageSize, setMembersPageSize] = useState(MEMBERS_PAGE_SIZE)
  const [isMembersLoading, setIsMembersLoading] = useState(Boolean(sessionId))
  const [membersError, setMembersError] = useState(null)
  const [searchQuery, setSearchQuery] = useState("")
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("")
  const [statusFilters, setStatusFilters] = useState("")
  const [sortBy, setSortBy] = useState(DEFAULT_SORT_BY)
  const [sortDirection, setSortDirection] = useState(DEFAULT_SORT_DIRECTION)
  const membersRef = useRef([])

  const [isExitOpen, setIsExitOpen] = useState(false)
  const [isLeaving, setIsLeaving] = useState(false)
  const [isCompletedOpen, setIsCompletedOpen] = useState(false)
  const [completedStats, setCompletedStats] = useState({
    time: "00:00:00",
    xp: "0",
  })
  const [isTimerUpdating, setIsTimerUpdating] = useState(false)
  const [isMutatingCollaborators, setIsMutatingCollaborators] = useState(false)
  const [impressions, setImpressions] = useState({})
  const [reactingMemberId, setReactingMemberId] = useState(null)
  const autoStartRef = useRef(null)
  const timelogInFlightRef = useRef(null)
  const sessionRef = useRef(session)
  sessionRef.current = session
  membersRef.current = members

  const currentUser = useSelector(state => state.auth.currentUser)
  const currentUserId =
    currentUser?.id != null ? String(currentUser.id) : ""

  const refreshSession = useCallback(async () => {
    if (!sessionId) return { ok: false, notFound: false }

    const sessionResponse = await get(endpoints.getSessionById(sessionId), {
      params: {
        "with[]": [
          "creator",
          "collaborators.user",
          "targets",
          "targets.targetable",
        ],
      },
    })

    if (!sessionResponse) {
      return { ok: false, notFound: false }
    }

    if (!sessionResponse?.data?.success || !sessionResponse?.data?.result) {
      return { ok: false, notFound: true }
    }

    const apiSession = sessionResponse.data.result
    let timelog = null

    // Timelog 403s when current_user_collaboration_status is null.
    if (canAccessSessionTimelog(apiSession?.current_user_collaboration_status)) {
      const timelogResponse = await get(endpoints.sessionTimelog(sessionId), {
        skipErrorToast: true,
      })
      if (timelogResponse?.data?.success) {
        timelog = timelogResponse.data.result
      }
    }

    const mapped = mapSessionDetail(apiSession, contextRef.current, timelog)
    setSession(mapped)
    setNotFound(false)
    setError(null)
    return { ok: true, mapped }
  }, [sessionId])

  const refreshTimelog = useCallback(
    async (timelogResult = null) => {
      if (!sessionId) return null

      let timelog = timelogResult
      if (!timelog) {
        const collabStatus = sessionRef.current?.collaborationStatus
        if (!canAccessSessionTimelog(collabStatus)) {
          return null
        }
        const timelogResponse = await get(endpoints.sessionTimelog(sessionId), {
          skipErrorToast: true,
        })
        if (!timelogResponse?.data?.success) return null
        timelog = timelogResponse.data.result
      }

      const timerState = mapTimelogResult(timelog)
      setSession(prev => {
        if (!prev) return prev
        const hasLeft = Boolean(prev.hasLeftSession)
        const sessionEnded =
          String(prev.status || "").toLowerCase() === "ended" ||
          Boolean(prev.endedAtRaw) ||
          timerState.timerStatus === "ended"
        const { timer, elapsedSeconds } = resolveSessionDisplayTimer({
          apiSession: {
            current_user_working_hours: prev.currentUserWorkingHours,
            total_working_hours: prev.totalWorkingHours,
            started_at: prev.startedAtRaw,
            ended_at: prev.endedAtRaw,
            status: prev.status,
          },
          timerState,
          sessionEnded,
          hasLeftSession: hasLeft,
          isSessionParticipant: prev.isSessionParticipant,
        })
        return {
          ...prev,
          ...timerState,
          timer,
          elapsedSeconds,
          isRunning: hasLeft ? false : timerState.isRunning,
          isOnBreak: hasLeft ? false : timerState.isOnBreak,
          canToggleTimer: hasLeft ? false : timerState.canToggleTimer,
          canExit: hasLeft ? false : timerState.canExit,
          canManageCollaborators: hasLeft
            ? false
            : prev.canManageCollaborators !== false,
          nextTimelogAction: hasLeft ? null : timerState.nextTimelogAction,
          timerStatus: hasLeft ? "ended" : timerState.timerStatus,
          isLive: hasLeft
            ? false
            : timerState.isRunning || timerState.isOnBreak,
          startedAt:
            !hasLeft && timerState.timerStatus !== "idle"
              ? prev.startedAtRaw
                ? formatDateTime(prev.startedAtRaw)
                : prev.startedAt
              : prev.startedAt || "—",
        }
      })
      return timerState
    },
    [sessionId],
  )

  useEffect(() => {
    let cancelled = false

    const loadSessionDetail = async () => {
      if (!sessionId) return

      setIsLoading(true)
      setError(null)
      setNotFound(false)

      try {
        const result = await refreshSession()
        if (cancelled) return

        if (!result.ok) {
          if (result.notFound) {
            setNotFound(true)
            setSession(null)
          } else {
            setError("Failed to load session detail")
            setSession(null)
          }
        }
      } catch (err) {
        if (cancelled) return
        const status = err?.response?.status
        if (status === 404) {
          setNotFound(true)
          setSession(null)
        } else {
          setError(
            err?.response?.data?.message || "Failed to load session detail",
          )
          setSession(null)
        }
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }

    loadSessionDetail()
    return () => {
      cancelled = true
    }
  }, [sessionId, refreshSession])

  useEffect(() => {
    autoStartRef.current = null
    if (sessionId) setIsLoading(true)
  }, [sessionId])

  useEffect(() => {
    setDisplayTimer(
      session?.timer || formatSeconds(session?.elapsedSeconds || 0),
    )
  }, [
    session?.id,
    session?.elapsedSeconds,
    session?.timer,
    session?.timerStatus,
  ])

  useEffect(() => {
    if (!session?.isRunning) return undefined

    let seconds = session?.elapsedSeconds || 0
    setDisplayTimer(formatSeconds(seconds))

    const intervalId = setInterval(() => {
      seconds += 1
      setDisplayTimer(formatSeconds(seconds))
    }, 1000)

    return () => clearInterval(intervalId)
  }, [session?.isRunning, session?.id, session?.elapsedSeconds])

  const postTimelogAction = useCallback(
    async (action, reason) => {
      if (!sessionId || !action) return false
      if (!canAccessSessionTimelog(sessionRef.current?.collaborationStatus)) {
        return false
      }

      if (timelogInFlightRef.current) {
        await timelogInFlightRef.current
      }

      setIsTimerUpdating(true)
      const request = (async () => {
        const response = await post(endpoints.sessionTimelog(sessionId), {
          action,
          reason: reason || TIMELOG_REASONS[action] || action,
        })

        if (!response?.data?.success) {
          if (response) {
            toast.error(response?.data?.message || "Failed to update timer", {
              position: "bottom-center",
              theme: "colored",
              transition: Bounce,
            })
          }
          return false
        }

        const postResult = response.data.result
        if (
          postResult?.state ||
          postResult?.totals ||
          postResult?.available_actions
        ) {
          await refreshTimelog(postResult)
        } else {
          await refreshTimelog()
        }
        return true
      })()

      timelogInFlightRef.current = request
      try {
        return await request
      } catch (err) {
        if (err?.response || err?.code) return false
        toast.error(err?.message || "Failed to update timer", {
          position: "bottom-center",
          theme: "colored",
          transition: Bounce,
        })
        return false
      } finally {
        if (timelogInFlightRef.current === request) {
          timelogInFlightRef.current = null
        }
        setIsTimerUpdating(false)
      }
    },
    [refreshTimelog, sessionId],
  )

  const handleTimerToggle = useCallback(async () => {
    const current = sessionRef.current
    if (!sessionId || isTimerUpdating || !current?.canToggleTimer) return false

    const action = current.nextTimelogAction
    if (!action) return false
    return postTimelogAction(action)
  }, [isTimerUpdating, postTimelogAction, sessionId])

  const handleExit = useCallback(() => {
    const current = sessionRef.current
    if (!current) return false

    // Open the confirm modal immediately — don't wait on timelog pause.
    setIsExitOpen(true)

    if (!current.isRunning && !timelogInFlightRef.current) return true

    ;(async () => {
      if (timelogInFlightRef.current) {
        await timelogInFlightRef.current
      }
      const latest = sessionRef.current
      if (latest?.isRunning) {
        await postTimelogAction("break", TIMELOG_REASONS.exit)
      }
    })()

    return true
  }, [postTimelogAction])

  useEffect(() => {
    if (!sessionId || isLoading || isTimerUpdating) return undefined
    if (autoStartRef.current === sessionId) return undefined
    if (!session || String(session.id) !== String(sessionId)) return undefined
    if (session.timerStatus !== "idle") return undefined
    if (session.nextTimelogAction !== "start") return undefined
    if (!session.canToggleTimer) return undefined

    autoStartRef.current = sessionId
    handleTimerToggle()
    return undefined
  }, [
    handleTimerToggle,
    isLoading,
    isTimerUpdating,
    session,
    sessionId,
  ])

  useEffect(() => {
    const timer = setTimeout(
      () => setDebouncedSearchQuery(searchQuery.trim()),
      SEARCH_DEBOUNCE_MS,
    )
    return () => clearTimeout(timer)
  }, [searchQuery])

  const handleSearchChange = value => {
    setSearchQuery(value)
    setMembersPage(1)
  }

  const handleStatusFiltersChange = next => {
    // Single-select: invited | joined | left (or clear).
    const value = Array.isArray(next)
      ? String(next[0] || "")
      : String(next || "")
    setStatusFilters(value)
    setMembersPage(1)
  }

  const handleSortByChange = next => {
    setSortBy(next)
    setMembersPage(1)
  }

  const handleSortDirectionChange = next => {
    setSortDirection(next)
    setMembersPage(1)
  }

  const fetchMembers = useCallback(
    async (overrides = {}) => {
      if (!sessionId) {
        setMembers([])
        setMembersTotal(0)
        setIsMembersLoading(false)
        return
      }

      const page = overrides.page ?? membersPage
      const search =
        overrides.search !== undefined
          ? overrides.search
          : debouncedSearchQuery
      const statuses =
        overrides.statusFilters !== undefined
          ? overrides.statusFilters
          : statusFilters

      setIsMembersLoading(true)
      setMembersError(null)

      try {
        const params = {
          "with[]": ["user"],
          page,
          per_page: MEMBERS_PAGE_SIZE,
          page_size: MEMBERS_PAGE_SIZE,
          sort_by: sortBy,
          sort_direction: sortDirection,
        }

        if (search) {
          params.search = search
        }

        // When filtering by status, request a wider page so client fallback
        // still sees invited/joined/left rows the API may not filter.
        if (statuses) {
          params.page = 1
          params.per_page = Math.max(MEMBERS_PAGE_SIZE, 100)
          params.page_size = params.per_page
        }

        // Single status: invited | joined | left
        if (statuses) {
          params.status = statuses
        }

        const response = await get(endpoints.getSessionCollaborators(sessionId), {
          params,
        })

        if (response?.data?.success) {
          const result = response.data.result || {}
          let rows = (result.data || []).map(mapSessionCollaborator)
          // Client fallback — API may ignore status; keep UI filter reliable.
          if (statuses) {
            rows = rows.filter(row =>
              matchesSessionCollabStatusFilters(row, statuses),
            )
          }
          const total = statuses
            ? rows.length
            : (result.total ?? rows.length)
          const perPage =
            result.per_page || result.page_size || MEMBERS_PAGE_SIZE

          setMembers(rows)
          setMembersTotal(total)
          setMembersPageSize(perPage)

          const totalPages = Math.max(1, Math.ceil(total / perPage))
          if (page > totalPages) {
            setMembersPage(totalPages)
          } else if (overrides.page != null) {
            setMembersPage(overrides.page)
          }
        } else {
          setMembersError(
            getApiErrorMessage(response, "Failed to load collaborators"),
          )
          setMembers([])
          setMembersTotal(0)
        }
      } catch (err) {
        setMembersError(getApiErrorMessage(err, "Failed to load collaborators"))
        setMembers([])
        setMembersTotal(0)
      } finally {
        setIsMembersLoading(false)
      }
    },
    [
      sessionId,
      membersPage,
      debouncedSearchQuery,
      statusFilters,
      sortBy,
      sortDirection,
    ],
  )

  const reloadSessionDetail = useCallback(async () => {
    if (!sessionId) return false
    setMembersPage(1)
    setSearchQuery("")
    setDebouncedSearchQuery("")
    setStatusFilters("")
    await Promise.all([
      refreshSession(),
      fetchMembers({ page: 1, search: "", statusFilters: "" }),
    ])
    return true
  }, [fetchMembers, refreshSession, sessionId])

  useEffect(() => {
    setMembersPage(1)
  }, [sessionId])

  useEffect(() => {
    fetchMembers()
  }, [fetchMembers])

  const statusFiltersRef = useRef(statusFilters)
  statusFiltersRef.current = statusFilters

  const handleCollaboratorUpdated = useCallback(collaborator => {
    const { next, added } = upsertSessionCollaboratorMember(
      membersRef.current,
      collaborator,
    )
    const filtered = next.filter(row =>
      matchesSessionCollabStatusFilters(row, statusFiltersRef.current),
    )
    membersRef.current = filtered
    setMembers(filtered)
    if (added) {
      setMembersTotal(prev => Math.max(0, Number(prev || 0) + 1))
    } else {
      setMembersTotal(filtered.length)
    }
  }, [])

  const handleCollaboratorLeft = useCallback(collaborator => {
    const { next, added } = upsertSessionCollaboratorMember(
      membersRef.current,
      collaborator,
      { left: true },
    )
    const filtered = next.filter(row =>
      matchesSessionCollabStatusFilters(row, statusFiltersRef.current),
    )
    membersRef.current = filtered
    setMembers(filtered)
    if (added) {
      setMembersTotal(prev => Math.max(0, Number(prev || 0) + 1))
    } else {
      setMembersTotal(filtered.length)
    }
  }, [])

  const handleCollaboratorReaction = useCallback(
    parsed => {
      if (!parsed?.collaboratorId) return

      const key = String(parsed.collaboratorId)
      const hasSummary = Array.isArray(parsed.reactionSummary)
      const counts = hasSummary
        ? countsFromReactionSummary(parsed.reactionSummary)
        : null
      const isMine =
        Boolean(currentUserId) &&
        Boolean(parsed.userId) &&
        String(parsed.userId) === String(currentUserId)

      setImpressions(prev => {
        const current = prev[key] || emptyImpression()
        const next = { ...current }

        if (counts) {
          next.likeCount = counts.likeCount
          next.heartCount = counts.heartCount
        }

        // Personal pressed state only for the reactor's own clients.
        if (isMine) {
          const personal =
            parsed.removed || !parsed.reaction
              ? { liked: false, hearted: false, reaction: null }
              : impressionFromReaction(parsed.reaction)
          next.liked = personal.liked
          next.hearted = personal.hearted
          next.reaction = personal.reaction
        }

        if (
          Boolean(current.liked) === Boolean(next.liked) &&
          Boolean(current.hearted) === Boolean(next.hearted) &&
          String(current.reaction || "") === String(next.reaction || "") &&
          Number(current.likeCount || 0) === Number(next.likeCount || 0) &&
          Number(current.heartCount || 0) === Number(next.heartCount || 0)
        ) {
          return prev
        }
        return { ...prev, [key]: next }
      })
    },
    [currentUserId],
  )

  useSessionCollaboratorRealtime({
    sessionId,
    enabled: Boolean(sessionId),
    onUpdated: handleCollaboratorUpdated,
    onLeft: handleCollaboratorLeft,
    onReaction: handleCollaboratorReaction,
  })

  const handleAddCollaborators = useCallback(
    async userIds => {
      if (!sessionId || isMutatingCollaborators) {
        return { ok: false, message: "" }
      }

      setIsMutatingCollaborators(true)
      try {
        const result = await addSessionCollaborators(sessionId, userIds)
        if (!result.ok) {
          const message = getApiErrorMessage(
            result.response,
            "Failed to add collaborators",
          )
          return { ok: false, message }
        }

        toast.success(
          result.response?.data?.message ||
            (userIds.length === 1
              ? "Collaborator added to session."
              : "Collaborators added to session."),
          {
            position: "bottom-center",
            theme: "colored",
            transition: Bounce,
          },
        )
        await reloadSessionDetail()
        return { ok: true }
      } catch (err) {
        return {
          ok: false,
          message: getApiErrorMessage(err, "Failed to add collaborators"),
        }
      } finally {
        setIsMutatingCollaborators(false)
      }
    },
    [isMutatingCollaborators, reloadSessionDetail, sessionId],
  )

  const handleRemoveCollaborator = useCallback(
    async member => {
      if (!sessionId || isMutatingCollaborators) return false

      const userId = member?.userId != null ? String(member.userId) : ""
      if (!userId) {
        toast.error("Unable to remove this collaborator.", {
          position: "bottom-center",
          theme: "colored",
          transition: Bounce,
        })
        return false
      }

      if (currentUserId && userId === currentUserId) {
        toast.error("Leave the session with Exit Session instead.", {
          position: "bottom-center",
          theme: "colored",
          transition: Bounce,
        })
        return false
      }

      setIsMutatingCollaborators(true)
      try {
        const result = await removeSessionCollaborators(sessionId, [userId])
        if (!result.ok) {
          const message = getApiErrorMessage(
            result.response,
            "Failed to remove collaborator",
          )
          if (message) {
            toast.error(message, {
              position: "bottom-center",
              theme: "colored",
              transition: Bounce,
            })
          }
          return false
        }

        toast.success(
          result.response?.data?.message ||
            `${member?.name || "Collaborator"} removed from session.`,
          {
            position: "bottom-center",
            theme: "colored",
            transition: Bounce,
          },
        )
        await reloadSessionDetail()
        return true
      } catch (err) {
        toast.error(
          getApiErrorMessage(err, "Failed to remove collaborator"),
          {
            position: "bottom-center",
            theme: "colored",
            transition: Bounce,
          },
        )
        return false
      } finally {
        setIsMutatingCollaborators(false)
      }
    },
    [currentUserId, isMutatingCollaborators, reloadSessionDetail, sessionId],
  )

  const handleLeaveSession = async ({ note }) => {
    if (!sessionId || isLeaving) return false

    const leaveNote = (note || "").trim()
    if (!leaveNote) {
      toast.error("Please add a summary note before exiting.", {
        position: "bottom-center",
        theme: "colored",
        transition: Bounce,
      })
      return false
    }

    setIsLeaving(true)
    try {
      const response = await post(endpoints.leaveSession(sessionId), {
        leave_note: leaveNote,
      })

      if (!response?.data?.success) {
        if (response) {
          toast.error(response?.data?.message || "Failed to leave session", {
            position: "bottom-center",
            theme: "colored",
            transition: Bounce,
          })
        }
        return false
      }

      const result = response.data.result || {}
      // Success popup "Time Logged" = this user's hours only (not session total).
      const loggedTime = resolveLeaveSessionLoggedTime(result, {
        currentUserWorkingHours:
          result.current_user_working_hours ??
          result.currentUserWorkingHours ??
          null,
      })
      const xpEarned = result.xp_earned ?? result.xp ?? "0"

      setCompletedStats({
        time: loggedTime,
        xp: String(xpEarned),
      })
      setIsExitOpen(false)
      setIsCompletedOpen(true)

      toast.success("You have left the session.", {
        position: "bottom-center",
        theme: "colored",
        transition: Bounce,
      })

      // Refresh listing after leave; do not use refreshed session totals for the modal.
      const refreshed = await refreshSession()
      const refreshedSession = refreshed?.mapped || sessionRef.current

      // Let sessions listing (and other listeners) update action labels
      // without waiting for a full page reload.
      if (typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent("session-left", {
            detail: {
              sessionId,
              endedAt:
                result.ended_at ||
                result.endedAt ||
                refreshedSession?.endedAtRaw ||
                null,
              timeLogged: loggedTime || null,
              totalWorkingHours:
                refreshedSession?.totalWorkingHours ??
                result.total_working_hours ??
                null,
              sessionStatus: refreshedSession?.status || result.status || null,
              collaborationStatus:
                refreshedSession?.collaborationStatus || "left",
            },
          }),
        )
      }
      return true
    } catch (err) {
      if (err?.response || err?.code) {
        return false
      }
      toast.error(err?.message || "Failed to leave session", {
        position: "bottom-center",
        theme: "colored",
        transition: Bounce,
      })
      return false
    } finally {
      setIsLeaving(false)
    }
  }

  const loadMemberImpressions = useCallback(
    async memberList => {
      if (!sessionId || !Array.isArray(memberList) || memberList.length === 0) {
        setImpressions({})
        return
      }

      const results = await Promise.all(
        memberList.map(async member => {
          const collaboratorId = member?.id
          if (!collaboratorId) return null
          const result = await getSessionCollaboratorReactions(
            sessionId,
            collaboratorId,
          )
          if (!result.ok) {
            return {
              id: String(collaboratorId),
              impression: emptyImpression(),
            }
          }
          const mine =
            result.rows.find(row => row?.is_current_user) ||
            result.rows.find(
              row =>
                currentUserId &&
                String(row?.user?.id || row?.user_id || "") ===
                  String(currentUserId),
            ) ||
            null
          const summary =
            result.result?.reaction_summary ||
            result.result?.reactionSummary ||
            null
          const counts = Array.isArray(summary)
            ? countsFromReactionSummary(summary)
            : countsFromReactionRows(result.rows)
          const personal = impressionFromReaction(mine?.reaction)
          return {
            id: String(collaboratorId),
            impression: {
              ...personal,
              likeCount: counts.likeCount,
              heartCount: counts.heartCount,
            },
          }
        }),
      )

      const next = {}
      results.forEach(item => {
        if (!item?.id) return
        next[item.id] = item.impression
      })
      setImpressions(next)
    },
    [currentUserId, sessionId],
  )

  useEffect(() => {
    if (!sessionId) {
      setImpressions({})
      return undefined
    }
    if (isMembersLoading) return undefined
    loadMemberImpressions(members)
    return undefined
  }, [sessionId, members, isMembersLoading, loadMemberImpressions])

  /**
   * Like (👍) and heart (❤️) are mutually exclusive.
   * - Click inactive → POST that reaction (switches off the other)
   * - Click active → DELETE that reaction
   */
  const handleToggleImpression = useCallback(
    async (memberId, type) => {
      if (!sessionId || !memberId || reactingMemberId) return false
      if (type !== "liked" && type !== "hearted") return false

      const key = String(memberId)
      const current = impressions[key] || emptyImpression()
      const targetEmoji =
        type === "liked"
          ? SESSION_COLLAB_REACTION.like
          : SESSION_COLLAB_REACTION.heart
      const isRemoving = Boolean(current[type])
      const likeCount = Number(current.likeCount || 0)
      const heartCount = Number(current.heartCount || 0)

      let optimistic
      if (isRemoving) {
        optimistic = {
          liked: false,
          hearted: false,
          reaction: null,
          likeCount:
            type === "liked" ? Math.max(0, likeCount - 1) : likeCount,
          heartCount:
            type === "hearted" ? Math.max(0, heartCount - 1) : heartCount,
        }
      } else {
        const personal = impressionFromReaction(targetEmoji)
        optimistic = {
          ...personal,
          likeCount:
            type === "liked"
              ? likeCount + (current.liked ? 0 : 1)
              : Math.max(0, likeCount - (current.liked ? 1 : 0)),
          heartCount:
            type === "hearted"
              ? heartCount + (current.hearted ? 0 : 1)
              : Math.max(0, heartCount - (current.hearted ? 1 : 0)),
        }
      }

      setReactingMemberId(key)
      setImpressions(prev => ({ ...prev, [key]: optimistic }))

      try {
        let result
        if (isRemoving) {
          result = await removeSessionCollaboratorReaction(
            sessionId,
            memberId,
            current.reaction || targetEmoji,
          )
        } else {
          // Switching like ↔ heart: POST the new reaction (replaces prior).
          result = await addSessionCollaboratorReaction(
            sessionId,
            memberId,
            targetEmoji,
          )
        }

        if (!result.ok) {
          setImpressions(prev => ({ ...prev, [key]: current }))
          toastApiError(result.response, "Failed to update impression")
          return false
        }

        if (!isRemoving) {
          celebrateImpression(type)
        }

        const summary =
          result.result?.reaction_summary ||
          result.result?.reactionSummary ||
          null
        if (Array.isArray(summary)) {
          const counts = countsFromReactionSummary(summary)
          setImpressions(prev => ({
            ...prev,
            [key]: {
              ...(prev[key] || optimistic),
              likeCount: counts.likeCount,
              heartCount: counts.heartCount,
            },
          }))
        }
        return true
      } catch (err) {
        setImpressions(prev => ({ ...prev, [key]: current }))
        toastApiError(err, "Failed to update impression")
        return false
      } finally {
        setReactingMemberId(null)
      }
    },
    [impressions, reactingMemberId, sessionId],
  )

  const gallery = members.map(m => ({
    id: m.id,
    name: m.name,
    src: m.avatar,
    status: m.galleryStatus,
  }))

  const existingCollaboratorUserIds = useMemo(
    () =>
      members
        .map(m => m.userId)
        .filter(Boolean)
        .map(String),
    [members],
  )

  return {
    session,
    displayTimer,
    isLoading,
    error,
    notFound,
    members,
    gallery,
    membersTotal,
    membersPage,
    membersPageSize,
    isMembersLoading,
    membersError,
    onMembersPageChange: setMembersPage,
    searchQuery,
    onSearchChange: handleSearchChange,
    statusFilters,
    onStatusFiltersChange: handleStatusFiltersChange,
    sortBy,
    onSortByChange: handleSortByChange,
    sortDirection,
    onSortDirectionChange: handleSortDirectionChange,
    isExitOpen,
    setIsExitOpen,
    isLeaving,
    isCompletedOpen,
    setIsCompletedOpen,
    completedStats,
    isTimerUpdating,
    isMutatingCollaborators,
    currentUserId,
    existingCollaboratorUserIds,
    impressions,
    reactingMemberId,
    onToggleImpression: handleToggleImpression,
    onAddCollaborators: handleAddCollaborators,
    onRemoveCollaborator: handleRemoveCollaborator,
    refreshMembers: fetchMembers,
    reloadSessionDetail,
    onTimerToggle: handleTimerToggle,
    onLeaveSession: handleLeaveSession,
    onExit: handleExit,
  }
}

export default useSessionDetail
